/**
 * One campaign (= one Lever posting) as four guided steps (Sofia, Oct 19 2026): 1 Brief · 2 Find
 * people · 3 Write & send · 4 Log (was Replies until Oct 24 2026). A step bar on top shows where you are and what is done, each
 * step says what to do, and ends with the way to the next one. The step lives in the URL (`?view=`)
 * like every board filter, because the board reloads itself after 15 idle minutes; the old
 * `?view=contacted` opens the Log, the old `?view=candidates` (Review) opens Write & send.
 *
 * There is no Review step any more (Sofia, Oct 19 2026): the shortlist already shows the full
 * profile check, so Accept there reads the profile if needed, queues the person and writes the draft.
 *
 * Long work runs as CLIENT loops, one request at a time: checking profiles and writing drafts are
 * model calls of 10-20 s each, and a server batch would hold a request open for minutes.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { showToast } from '@finn-auto/design-system/atoms/toast';
import { logAnalyticsEvent } from '../../../../analytics/nativeAnalytics';
import * as api from '../../services/taOutreachApi';
import type { Campaign, OutreachCandidate, OutreachConfig } from '../../types/taOutreach';
import { useOptionalGmailConnection } from '../taActionBoard/GmailConnectionContext';
import { OutreachBriefPanel } from './OutreachBriefPanel';
import { OutreachCard } from './OutreachCard';
import { RepliesPanel } from './RepliesPanel';
import { OutreachLog } from './OutreachLog';
import { Button } from '@finn-auto/design-system/atoms/button';
import { SendAsBar } from './SendAsBar';
import { FunnelStrip, QueueSummary, StepIntro, Stepper, type Step } from './outreachUi';
import { ExtensionSend } from './ExtensionSend';
import { BriefSourceProblemBox } from './BriefSourceProblemBox';
import { SourcingPanel } from './SourcingPanel';
import { DroppedList } from './DroppedList';
import { useOutreachImport } from './useOutreachImport';
import { canCapture, extensionVersion } from './outreachExtension';
import { buildLog, LOG_STAGE_LABEL, LOG_STAGES } from './logStages';
import { UpdateAllDrafts } from './UpdateAllDrafts';
import { colleaguesSigned, runInLanes } from './updateAll';
import { BulkApprove } from './BulkApprove';
import { ReplyCheckNow } from './ReplyCheckNow';
import { useBulkSelection } from './useBulkSelection';
import { useSharedBrief } from './useSharedBrief';
import { onePerPerson } from './onePerPerson';
import { useAutoDraft, waitingPeople } from './useAutoDraft';
import { useDrafting } from './useDrafting';
import { UnreadProfilesBox } from './UnreadProfilesBox';
import { readAcceptedInBackground } from './acceptHandOff';
import { auth } from '../../../../firebase';

export type CampaignSub = 'brief' | 'sourcing' | 'candidates' | 'queue' | 'contacted' | 'replies';
type StepId = Exclude<CampaignSub, 'contacted' | 'candidates'>;
const STEP_ORDER: StepId[] = ['brief', 'sourcing', 'queue', 'replies'];
const IN_QUEUE = new Set(['queued', 'drafted', 'ready']);

export function CampaignView({
  postingId,
  campaign,
  config,
  sub,
  onSub,
  focusId,
  onBack,
  onCountChange,
}: {
  postingId: string;
  campaign: Campaign | null;
  config: OutreachConfig;
  sub: CampaignSub;
  onSub: (s: CampaignSub) => void;
  focusId: string | null;
  onBack: () => void;
  onCountChange?: (n: number) => void;
}) {
  const gmail = useOptionalGmailConnection();
  const [items, setItems] = useState<OutreachCandidate[]>([]);
  const [loading, setLoading] = useState(true);
  const [compiling, setCompiling] = useState(false);
  const [briefProblem, setBriefProblem] = useState<api.BriefSourceProblem | null>(null);
  const [captureOk, setCaptureOk] = useState(false);
  const [, setChecking] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState<string | null>(focusId);
  const busyLoop = useRef(false);
  const campaignTitle = campaign?.title;
  // The role's one brief, shared by every recruiter (Oct 27 2026).
  const { brief, showBrief, saveBrief } = useSharedBrief(postingId, campaignTitle);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.fetchOutreach(postingId, campaignTitle);
      setItems(r.items);
      showBrief(r.brief);
    } catch (err) {
      showToast({ status: 'error', position: 'bottomCenter', description: err instanceof Error ? err.message : 'Could not load this campaign.' });
    } finally {
      setLoading(false);
    }
  }, [postingId, campaignTitle, showBrief]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- fetching on mount is the external sync
    void load();
  }, [load]);
  useEffect(() => {
    let alive = true;
    void extensionVersion().then((v) => alive && setCaptureOk(canCapture(v)));
    return () => {
      alive = false;
    };
  }, []);

  // Routes answer with the stored row; follow-up preview and test flag are kept until the next full read.
  const replace = (item: OutreachCandidate) =>
    setItems((list) =>
      list.map((c) =>
        c.id !== item.id
          ? c
          : item.followUpPreview !== undefined || item.status === 'sent' || item.status === 'dropped'
            ? { ...item, testRecipient: item.testRecipient ?? c.testRecipient }
            : { ...item, followUpPreview: c.followUpPreview, followUpEdited: c.followUpEdited, testRecipient: item.testRecipient ?? c.testRecipient }
      )
    );
  const mark = (setter: typeof setChecking, id: string, on: boolean) =>
    setter((s) => {
      const next = new Set(s);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });

  // Drafts are written in useDrafting; the auto-draft's come back approved when clean (Nov 2 2026).
  const { drafting, draftOne, queueAndDraft } = useDrafting(replace);
  const shortlisted = items.filter((c) => c.status === 'shortlisted');
  const queue = items.filter((c) => IN_QUEUE.has(c.status));
  const bulk = useBulkSelection(queue, drafting, Boolean(brief?.brief));
  const ready = items.filter((c) => c.status === 'ready');
  // Their own row wins when a person sits on the list twice (Oct 28 2026).
  const viewer = `${auth.currentUser?.email || ''}`.toLowerCase();
  // Only the viewer's OWN drafts are `sentAsStale` (Oct 28 2026); a colleague's are `sentAsOther`.
  const stale = items.filter((c) => c.sentAsStale && (c.status === 'drafted' || c.status === 'ready'));
  // Older wording the proxy could not put together again without the model (Oct 27 2026).
  const outdated = items.filter((c) => c.draftOutdated && !c.sentAsStale && (c.status === 'drafted' || c.status === 'ready'));
  // Accepted people without a draft, one row per person (Oct 28 2026).
  const { toRead, toDraft } = waitingPeople(items, viewer);
  const drafts = items.filter((c) => c.status === 'drafted' || c.status === 'queued');
  const sent = items.filter((c) => c.status === 'sent');
  const rejected = items.filter((c) => c.status === 'dropped' && c.dropReason === 'rejected');
  const skipped = items.filter((c) => c.status === 'dropped' && c.dropReason !== 'rejected');
  const [restoring, setRestoring] = useState<string | null>(null);
  /** Undo a reject (back onto the shortlist) or a skip (read again, queued, drafted). */
  async function restore(c: OutreachCandidate) {
    if (restoring) return; // one at a time: each reads a profile through the extension
    setRestoring(c.id);
    const r = await api.moveOutreach(c.id, 'restore').catch((err: unknown) => {
      showToast({ status: 'error', position: 'bottomCenter', description: err instanceof Error ? err.message : 'Could not bring them back.' });
      return null;
    });
    if (r) replace(r.item);
    if (r?.item.status === 'imported') await acceptToQueue(r.item);
    setRestoring(null);
  }
  const [logNow] = useState(() => Date.now());
  const log = buildLog(items, logNow, brief?.followUpAfterDays ?? 5);
  const replied = sent.filter((c) => c.reply).length;
  // Waiting on a reply or a booking (/sync hands booked rows back; CodeRabbit, PR #781).
  const hasOpenSent = sent.some((c) => !c.reply || !c.booking);
  const [leverCheck, setLeverCheck] = useState<{ at: number; available: boolean } | null>(null);
  const [unmatchedMail, setUnmatchedMail] = useState<api.ReplyNotification[]>([]);
  const [mailCheck, setMailCheck] = useState<{ at: number; error?: string } | null>(null);
  const gmailToken = gmail?.token ?? null;

  // Replies: LinkedIn's notification mails in the recruiter's own Gmail (read only on the proxy).
  const syncMail = useCallback(
    async (force = false) => {
      if (!gmailToken) return;
      try {
        const r = await api.syncReplyMail(postingId, gmailToken, force);
        if (r.throttled) return;
        if (r.items.length) setItems((list) => list.map((c) => r.items.find((u) => u.id === c.id) || c));
        setUnmatchedMail(r.unmatched);
        setMailCheck({ at: Date.now() });
      } catch (err) {
        setMailCheck({ at: Date.now(), error: err instanceof Error ? err.message : 'Could not read Gmail.' });
      }
    },
    // setUnmatchedMail is listed because the compiler infers it as a dependency here and refuses
    // to preserve the memo without it (it is stable, so this changes nothing at runtime).
    [postingId, gmailToken, setUnmatchedMail]
  );

  // What Lever knows about the sent InMails (RSC stage changes): on opening the queue, then every
  // five minutes while it is open. The proxy only READS Lever; a reply closes the follow-up.
  const syncLever = useCallback(
    async (force = false) => {
      try {
        const r = await api.syncLever(postingId, force);
        if (r.items.length) setItems((list) => list.map((c) => r.items.find((u) => u.id === c.id) || c));
        if (!r.throttled) setLeverCheck({ at: Date.now(), available: r.available });
      } catch {
        setLeverCheck({ at: Date.now(), available: false });
      }
    },
    [postingId]
  );
  useEffect(() => {
    if (loading || !['queue', 'replies', 'contacted'].includes(sub) || !hasOpenSent) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- polling Lever and Gmail is the external sync
    void syncLever();
    void syncMail();
    const t = setInterval(() => {
      void syncLever();
      void syncMail();
    }, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, [loading, sub, hasOpenSent, syncLever, syncMail]);
  useEffect(() => onCountChange?.(drafts.length), [drafts.length, onCountChange]);

  async function analyseOne(id: string) {
    mark(setChecking, id, true);
    try {
      replace((await api.analyseOutreach(id)).item);
    } catch (err) {
      showToast({ status: 'error', position: 'bottomCenter', description: err instanceof Error ? err.message : 'Could not check that profile.' });
    } finally {
      mark(setChecking, id, false);
    }
  }

  /** One request at a time, or `parallel` (Oct 28 2026); false when another loop is running and nothing was done. */
  async function sequential(ids: string[], fn: (id: string) => Promise<void>, { parallel = 1 }: { parallel?: number } = {}) {
    if (busyLoop.current) return false;
    busyLoop.current = true;
    try {
      await runInLanes(ids, fn, parallel);
    } finally {
      busyLoop.current = false;
    }
    return true;
  }

  const imports = useOutreachImport({
    postingId,
    roleTitle: campaign?.title || brief?.roleTitle || '', // hiring plan names the role (Oct 22 2026)
    hasBrief: Boolean(brief?.brief),
    setItems,
    replace,
    checkAll: (ids) => void sequential(ids, analyseOne),
  });

  /**
   * Accept on the shortlist = write to them (no Review step since Oct 19 2026): read the full
   * profile if sourcing did not, add them to the queue, write the draft (the draft runs the check).
   */
  async function acceptToQueue(item: OutreachCandidate) {
    let c: OutreachCandidate | null = item;
    if (!`${c.profileText || ''}`.trim()) {
      if (!captureOk) {
        showToast({ status: 'error', position: 'bottomCenter', description: `${c.name}: the FINN Outreach Sender is needed to read the profile. Install it, then accept again.` });
        return;
      }
      c = await imports.captureOne(c);
      if (!c) return;
    }
    if (brief?.brief) return queueAndDraft(c.id, c.status === 'queued', { auto: true }); // an accept: approved by itself when clean
    if (c.status === 'queued') return;
    try {
      replace((await api.moveOutreach(c.id, 'queue')).item);
    } catch (err) {
      showToast({ status: 'error', position: 'bottomCenter', description: err instanceof Error ? err.message : 'Could not add to outreach.' });
    }
  }

  async function compile(force: boolean, token: string | null = gmail?.token ?? null) {
    setCompiling(true);
    try {
      const r = await api.compileBrief(postingId, token, force);
      setBriefProblem(null);
      showBrief(r.brief);
      void logAnalyticsEvent('ta_outreach_brief_compiled', { criteria: r.brief.brief?.mustHave.length ?? 0, kickoff: Boolean(r.brief.source?.kickoff), cached: r.cached });
    } catch (err) {
      const problem = api.briefSourceProblem(err);
      setBriefProblem(problem);
      if (!problem) showToast({ status: 'error', position: 'bottomCenter', description: err instanceof Error ? err.message : 'Could not build the brief.' });
    } finally {
      setCompiling(false);
    }
  }

  /** Connect Google, then build again with the fresh token — the reason to connect was the brief. */
  async function connectAndRebuild() {
    const token = (await gmail?.connect()) ?? null;
    if (token) await compile(Boolean(brief?.brief), token);
  }

  const title = campaign?.title || brief?.roleTitle || 'Campaign';
  const step: StepId = sub === 'contacted' ? 'replies' : sub === 'candidates' ? 'queue' : sub;

  // Any step of the role (Nov 2 2026): drafting starts at Accept, 3 at once, beside the other loops.
  useAutoDraft({ toDraft, active: !loading && Boolean(brief?.brief), draft: (id, queued) => queueAndDraft(id, queued, { auto: true }) });
  const steps: Step<StepId>[] = [
    { id: 'brief', label: 'Brief', hint: 'What the role needs', done: Boolean(brief?.brief) },
    { id: 'sourcing', label: 'Find people', hint: 'Search LinkedIn Recruiter', done: items.length > 0, count: shortlisted.length || null },
    { id: 'queue', label: 'Write & send', hint: 'Approve and send InMails', done: sent.length > 0, count: queue.length || null },
    { id: 'replies', label: 'Log', hint: 'Where everyone stands', done: replied > 0, count: sent.length || null },
  ];
  const stepNo = STEP_ORDER.indexOf(step) + 1;
  const next = steps[stepNo];
  const nextStep = next ? (
    <div className="mt-6 flex flex-wrap items-center justify-end gap-3 border-t border-solid border-[#E9EAEC] pt-4">
      {!steps[stepNo - 1].done && <span className="text-[13px] text-[#707070]">Not done here yet — you can still go on.</span>}
      <Button size="sm" variant={steps[stepNo - 1].done ? 'primary' : 'outline'} onClick={() => onSub(next.id)}>
        Next: {stepNo + 1} · {next.label} →
      </Button>
    </div>
  ) : null;
  return (
    <div>
      <button type="button" onClick={onBack} className="text-xs font-semibold text-[#0072EA] underline">
        ← All campaigns
      </button>
      <h2 className="mt-1.5 text-[22px] font-semibold leading-[30px]">{title}</h2>
      <p className="mb-[18px] mt-1 text-[13px] text-[#707070]">
        {[campaign?.department, campaign?.status, campaign?.owner ? `Owner ${campaign.owner}` : null].filter(Boolean).join(' · ')}
      </p>
      {/* The same stages and counts as the Log (Sofia, Oct 24 2026; Booked since Nov 2 2026). */}
      <FunnelStrip items={LOG_STAGES.map((s) => ({ label: LOG_STAGE_LABEL[s], value: log[s].length }))} />
      <Stepper<StepId> steps={steps} value={step} onChange={onSub} />
      {loading && <p className="text-[13px] text-[#707070]">Loading…</p>}
      {!loading && (
        <StepIntro n={stepNo} total={STEP_ORDER.length}>
          {
            {
              brief: 'Check the brief the agent built from the Lever posting and the kick-off sheet. Fix the must-haves if needed: finding people and every profile check use them.',
              sourcing:
                'Add your LinkedIn Recruiter search and press Start sourcing. The agent reads the list, checks everyone against the brief and puts the fits on the shortlist. Accept the ones you want to write to: their InMail is drafted right away, in the next step.',
              queue: 'Drafts without a warning approve themselves; read the rest, edit if needed and approve. Start then sends the approved InMails from your own LinkedIn Recruiter.',
              replies:
                'Where everyone on this role stands: shortlisted, InMail sent, follow-up sent, replied, booked. Booking a call hands them over to Lever — the outreach for them ends there.',
            }[step]
          }
        </StepIntro>
      )}
      {!loading && step === 'brief' && briefProblem && (
        <BriefSourceProblemBox problem={briefProblem} connecting={Boolean(gmail?.connecting)} onConnectGmail={gmail ? () => void connectAndRebuild() : undefined} />
      )}
      {!loading && step === 'brief' && gmail?.error && (
        <p role="alert" className="text-[13px] text-[#E71D3F]">
          Google could not be connected: {gmail.error}
        </p>
      )}
      {!loading && step === 'brief' && (
        <OutreachBriefPanel
          brief={brief}
          compiling={compiling}
          gmailConnected={Boolean(gmail?.connected)}
          onConnectGmail={() => void connectAndRebuild()}
          onCompile={(force) => void compile(force)}
          onSave={saveBrief}
        />
      )}

      {!loading && step === 'sourcing' && (
        <SourcingPanel
          postingId={postingId}
          roleTitle={campaign?.title || brief?.roleTitle || ''}
          shortlisted={shortlisted}
          rejected={rejected}
          restoring={restoring}
          onRestore={(c) => void restore(c)}
          onUpdate={replace}
          onAccepted={(accepted) => readAcceptedInBackground(accepted, { captureOk, captureOne: imports.captureOne, hasBrief: Boolean(brief?.brief), onUpdated: replace })}
          onAdded={(added) =>
            setItems((list) => [...list.map((c) => added.find((a) => a.id === c.id) || c), ...added.filter((a) => !list.some((c) => c.id === a.id))])
          }
        />
      )}

      {!loading && step === 'queue' && (
        <div className="space-y-4">
          <SendAsBar stale={onePerPerson(stale, viewer)} onSaved={() => void load()} />
          <div className="flex flex-wrap items-center gap-2.5 rounded-md border border-solid border-[#E9EAEC] bg-[#F8F8F8] px-[18px] py-[13px] text-[13px]">
            <QueueSummary drafts={drafts} ready={ready} />
            <span className="flex-1" />
            {sent.length > 0 && (
              <ReplyCheckNow
                leverCheck={leverCheck}
                onCheck={() => {
                  void syncLever(true);
                  void syncMail(true);
                }}
              />
            )}
            {queue.some((c) => c.status === 'queued' && !drafting.has(c.id)) && (
              <button
                type="button"
                className="h-8 rounded-md border border-solid border-[#1A1A1A] bg-white px-3.5 text-xs font-semibold hover:bg-[#1A1A1A] hover:text-white"
                onClick={() => void sequential(queue.filter((c) => c.status === 'queued').map((c) => c.id), async (id) => void (await draftOne(id)))}
              >
                Write missing drafts
              </button>
            )}
            <BulkApprove selection={bulk} onUpdated={replace} />
            {/* Own drafts for another sender and older wording: one button (Oct 28 2026). */}
            <UpdateAllDrafts rows={[...stale, ...outdated, ...colleaguesSigned(items)]} run={sequential} onUpdated={replace} redraft={draftOne} />
          </div>
          {/* Read profiles are drafted by themselves; only the extension can read the rest (Oct 28 2026). */}
          <UnreadProfilesBox
            people={toRead}
            disabled={!brief?.brief}
            onRead={() =>
              void sequential(toRead.map((c) => c.id), async (id) => {
                const c = toRead.find((x) => x.id === id);
                if (c) await acceptToQueue(c);
              })
            }
          />
          {queue.map((c) => (
            <OutreachCard
              key={c.id}
              candidate={c}
              config={config}
              expanded={expanded === c.id}
              drafting={drafting.has(c.id)}
              onToggle={() => setExpanded((cur) => (cur === c.id ? null : c.id))}
              onDraft={(template) => void draftOne(c.id, template)}
              onUpdated={replace}
              select={bulk.boxFor(c)}
            />
          ))}
          {!queue.length && <p className="text-[13px] text-[#707070]">Nothing to write yet. Accept people on the shortlist in step 2 (Find people).</p>}
          {/* Written for another sender or caller, or with an older version: held until updated (Oct 26/27 2026). */}
          <ExtensionSend items={ready.filter((c) => !c.sentAsStale && !c.draftOutdated)} onUpdate={replace} followUpDays={brief?.followUpAfterDays ?? 5} />
          <DroppedList title="Skipped" people={skipped} restoring={restoring} onRestore={(c) => void restore(c)} restoreLabel="Write to them again" />
        </div>
      )}

      {!loading && step === 'replies' && (
        <RepliesPanel
          postingId={postingId}
          sent={sent}
          gmailConnected={Boolean(gmail?.connected)}
          onConnectGmail={() => void gmail?.connect()}
          mailCheck={mailCheck}
          unmatched={unmatchedMail}
          onCheckNow={() => void syncMail(true)}
          onReplied={(item, threadId) => {
            replace(item);
            setUnmatchedMail((list) => list.filter((x) => x.threadId !== threadId));
          }}
          onIgnored={(threadId) => setUnmatchedMail((list) => list.filter((x) => x.threadId !== threadId))}
        />
      )}
      {!loading && step === 'replies' && (
        <div className="mt-6">
          <OutreachLog
            items={items}
            config={config}
            followUpDays={brief?.followUpAfterDays ?? 5}
            expanded={expanded}
            onToggle={(id) => setExpanded((cur) => (cur === id ? null : id))}
            onUpdated={replace}
          />
        </div>
      )}
      {!loading && nextStep}
    </div>
  );
}
